import ActionCrossSign from "assets/img/svg/actionCrossSign";
import Checkmark from "assets/img/svg/checkmark";
import SendEmail from "assets/img/svg/sendEmail";
import StyledButton from "components/shared/StyledButton";
import { useActiveSignProvider } from "hooks/useActiveSignProvider";
import { useTranslation } from "react-i18next";
import type { AgreementValidator } from "store/agreementsStore";
import { AgreementStatus, AgValidatorStatus } from "store/agreementsStore";
import type { IUserData } from "store/settingsStore";
import type { TFetchStatus } from "store/types";

import { useAgreementPageContext } from "../context/StateProvider";

interface IContractValidationFooter {
    agreementStatus: TFetchStatus;
    /**
     * State to render loading btn animate
     */
    isLoadingButton: "approve" | "decline" | "send-for-validation" | null;
    /**
     * Function on sending for validation or signature btn
     */
    handleValidate: (status: AgreementStatus | "approve" | "decline") => void;
    /**
     * Verify if agreement has validators
     */
    hasValidators: boolean;
    agreementValidators: AgreementValidator[];
    userData: IUserData | null;
    /**
     * Verify if the user is the last validator of the contract
     */
    isLastValidator: boolean;
    /**
     * Open validation modal
     */
    handleShowApproveAndSendModal: () => void;
}

const ContractValidationFooter = ({
    agreementStatus,
    isLoadingButton,
    handleValidate,
    hasValidators,
    agreementValidators,
    userData,
    isLastValidator,
    handleShowApproveAndSendModal,
}: IContractValidationFooter) => {
    const [t] = useTranslation();
    const activeSignProvider = useActiveSignProvider();
    const { agreement, isDraft, isOwner } = useAgreementPageContext();

    const isValidatorWaiting = agreementValidators?.find(
        (user) => user.userId == userData?.id && user.status === AgValidatorStatus.WAITING,
    );

    return (
        <>
            {agreementStatus == "succeeded" && agreement?.status !== AgreementStatus.SENT && (
                <div className="contract-validation-edit-footer">
                    {isDraft && isOwner && (
                        <StyledButton
                            className="contract-validation-edit-footer-button"
                            buttonType="primary"
                            loading={isLoadingButton === "send-for-validation"}
                            disabled={!!isLoadingButton}
                            onClick={() => handleValidate(agreement?.status ?? AgreementStatus.DRAFT)}
                        >
                            <SendEmail />
                            {hasValidators
                                ? t("ContractValidationEdit.SendForValidation")
                                : t("ContractValidationEdit.SendForSignatureVia", {
                                      provider: activeSignProvider.label,
                                  })}
                        </StyledButton>
                    )}
                    {agreement?.status == AgreementStatus.NEGOTIATING && isValidatorWaiting && (
                        <div style={{ display: "flex", gap: "16px" }}>
                            <StyledButton
                                loading={isLoadingButton === "decline"}
                                buttonType="outlined"
                                fullWidth
                                onClick={() => handleValidate("decline")}
                            >
                                <ActionCrossSign />
                                {t("ContractValidationEdit.Decline")}
                            </StyledButton>
                            <StyledButton
                                loading={isLoadingButton === "approve"}
                                buttonType="primary"
                                fullWidth
                                onClick={() =>
                                    isLastValidator ? handleShowApproveAndSendModal() : handleValidate("approve")
                                }
                            >
                                <Checkmark />
                                {isLastValidator
                                    ? t("ContractValidationEdit.ApproveAndSendForSignatureVia", {
                                          provider: activeSignProvider.label,
                                      })
                                    : t("ContractValidationEdit.Approve")}
                            </StyledButton>
                        </div>
                    )}
                </div>
            )}
        </>
    );
};

export default ContractValidationFooter;
